import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import "./EventList.css";
import "./EventAnketoMan.css";
import AdminEventTabs from "./AdminEventTabs";

// 日付設定 (例: 04/10)
const formatDate = (isoDate) => {
    if (!isoDate) return "MM/DD";
    const datePart = isoDate.split("T")[0];
    return datePart.substring(5).replace("-", "/");
};

// 時間設定 (例: 19:30)
const formatTime = (iso) => {
    if (!iso) return "--:--";
    const t = iso.includes("T") ? iso.split("T")[1] : iso;
    return t.substring(0, 5);
};

const PAGE_SIZE = 10;

const EventAnketoMan = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [sortOrder, setSortOrder] = useState("desc"); // 最新版前
    // 中止したイベントのアンケートは既定では隠す（イベント一覧の状態と揃える）
    const [filterStatus, setFilterStatus] = useState("active");
    const [currentPage, setCurrentPage] = useState(1);
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();

    const fetchEvents = async (isManualRefresh = false) => {
        if (isManualRefresh) setRefreshing(true);
        else setLoading(true);
        setError(null);
        try {
            // イベントのリストを取得（キャッシュバスター付き）
            const res = await api.get(`/events/?_t=${Date.now()}`);
            const rawData = Array.isArray(res.data) ? res.data : (res.data.results || []);

            // 各イベントについて、アンケート回答者数を取得
            const eventsWithStats = await Promise.all(
                rawData.map(async (event) => {
                    let stats = { respondents: 0 };
                    try {
                        const resRes = await api.get(`/events/${event.id}/get_responses/?_t=${Date.now()}`);
                        const responses = Array.isArray(resRes.data) ? resRes.data : [];
                        stats.respondents = responses.length;
                    } catch (e) {
                        console.error("アンケート回答者取得エラー:", event.id);
                    }
                    return { ...event, stats };
                })
            );
            setEvents(eventsWithStats);
        } catch (err) {
            setError("イベントを取得できませんでした。");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchEvents();
        // 60秒ごとに自動更新
        const timer = setInterval(() => fetchEvents(), 60000);
        return () => clearInterval(timer);
    }, []); // fetchEvents は初回マウント時のみ実行（タイマーで60秒ごとに自動更新）

    /**
     * アンケートの削除。
     * 回答を削除して hasAnketo を False にするため、この一覧からも消える。
     * 中止・終了したイベントのアンケートを片付けるための操作。
     */
    const handleDeleteAnketo = async (event, clickEv) => {
        clickEv.stopPropagation();
        const nameLabel = event.eventName || "このイベント";
        const count = event.stats?.respondents ?? 0;
        if (!window.confirm(
            `「${nameLabel}」のアンケートを削除しますか？\n` +
            `回答 ${count} 件が削除され、この一覧から表示されなくなります。\n` +
            "この操作は取り消せません。"
        )) {
            return;
        }
        setDeletingId(event.id);
        try {
            await api.post(`/events/${event.id}/delete_anketo/`);
            setEvents((prev) => prev.filter((ev) => ev.id !== event.id));
        } catch (err) {
            alert(err.response?.data?.error || "アンケートの削除に失敗しました。");
        } finally {
            setDeletingId(null);
        }
    };

    // フィルタリングとソートをまとめて処理
    const filtered = useMemo(() => {
        let list = Array.isArray(events) ? [...events] : [];

        // 1. 公開されていてアンケートがあるイベントのみ表示
        list = list.filter(e => e.status === "published" && e.hasAnketo === true);

        // 2. 中止状態で絞り込み（既定は中止済みを除いた「実施中」）
        if (filterStatus === "active") {
            list = list.filter(e => !e.is_cancelled);
        } else if (filterStatus === "cancelled") {
            list = list.filter(e => e.is_cancelled === true);
        }

        // 3. サーチボックスでフィルタリング
        if (searchText.trim()) {
            const q = searchText.trim().toLowerCase();
            list = list.filter(e => (e.eventName || "").toLowerCase().includes(q));
        }

        // 4. 日付でソート
        list.sort((a, b) => {
            const da = new Date(a.eventDate || 0);
            const db = new Date(b.eventDate || 0);
            return sortOrder === "asc" ? da - db : db - da;
        });

        return list;
    }, [events, searchText, sortOrder, filterStatus]);

    const totalPages = Math.max(1, Math.ceil((filtered?.length || 0) / PAGE_SIZE));
    const paginated = (filtered || []).slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    if (loading) return <div className="event-list-container el-anketo-man"><p className="loading-text">Loading...</p></div>;
    if (error) return <div className="event-list-container el-anketo-man"><p className="error-text">{error}</p></div>;

    return (
        <div className="event-list-container el-anketo-man">
            <AdminEventTabs active="anketo" />
            <div className="el-header">
                {/*<button className="el-btn-back" onClick={() => navigate("/admin/create_event")}> 前画面に戻る</button> */}
                <h2 className="el-title">アンケート管理</h2>
                <button
                    className="el-btn-back"
                    onClick={() => fetchEvents(true)}
                    disabled={refreshing}
                    style={{ marginLeft: "auto", background: refreshing ? "#9ca3af" : "#2563eb", minWidth: 100 }}
                >
                    {refreshing ? "更新中..." : "🔄 再読み込み"}
                </button>
            </div>

            <div className="el-toolbar">
                <input
                    type="text"
                    className="el-search"
                    placeholder="イベントを検索..."
                    value={searchText}
                    onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
                />
                <select
                    className="el-select"
                    value={filterStatus}
                    onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                >
                    <option value="active">実施中のイベント</option>
                    <option value="cancelled">中止済みのイベント</option>
                    <option value="all">すべて</option>
                </select>
                <select className="el-select" value={sortOrder} onChange={e => { setSortOrder(e.target.value); setCurrentPage(1); }}>
                    <option value="desc">最新のイベントから</option>
                    <option value="asc">古いイベントから</option>
                </select>
                <span className="el-count">数量: {filtered.length}</span>
            </div>

            {paginated.length === 0 ? (
                <p className="no-events-message">該当する調査データがありません。</p>
            ) : (
                <div className="el-list">
                    {paginated.map((event) => (
                        <div
                            key={event.id}
                            className={`el-card${event.is_cancelled ? " el-card-cancelled" : ""}`}
                        >
                            <div className="el-card-layout">
                                <div className="el-card-col-left">
                                   
                                    <div className="el-card-row el-card-row-title">
                                        <span className="el-event-name">{event.eventName}</span>
                                        {event.is_cancelled && (
                                            <span className="el-badge el-badge-cancelled">中止済み</span>
                                        )}
                                        {/*<span className="el-badge el-badge-published">公開済み</span>*/}
                                    </div>
                                    
                                    <div className="el-card-row el-card-row-middle">
                                        <div className="el-card-meta">
                                            <span className="el-meta-item">
                                                <span className="el-meta-label">開催日</span>
                                                {formatDate(event.eventDate)}
                                            </span>
                                            <span className="el-meta-item">
                                                <span className="el-meta-label">時間</span>
                                                {formatTime(event.startTime)} 〜 {formatTime(event.endTime)}
                                            </span>
                                            {event.location && (
                                                <span className="el-meta-item">
                                                    <span className="el-meta-label">場所</span>
                                                    {event.location.length > 30 ? event.location.substring(0, 30) + "…" : event.location}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* 中止済みは理由も出す。結果を見る前に中止済みだと分かるようにする */}
                                    {event.is_cancelled && (
                                        <div className="el-card-row el-card-row-cancelled">
                                            <p className="el-anketo-cancelled-note">
                                                <span className="el-cancelled-panel-label">中止理由</span>
                                                {event.cancel_reason || "中止理由は登録されていません"}
                                            </p>
                                        </div>
                                    )}

                                    <div className="el-card-row el-card-row-stats">
                                        <div className="el-stats">
                                            <span className="el-stat el-stat-yes">
                                                参加者: {event.stats?.respondents ?? 0}人
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="el-card-col-actions el-card-col-actions--three"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                >
                                    <div className="el-action-slot el-action-slot-top">
                                        <Link
                                            to={`/event/${event.id}/get_responses`}
                                            className="el-btn-action el-btn-anketo"
                                        >
                                            アンケート結果
                                        </Link>
                                    </div>
                                    <div className="el-action-slot el-action-slot-mid">
                                        <Link
                                            to={`/event/${event.id}/statistics`}
                                            className="el-btn-action el-btn-stats"
                                        >
                                            集計
                                        </Link>
                                    </div>
                                    <div className="el-action-slot el-action-slot-bot">
                                        <button
                                            type="button"
                                            className="el-btn-action el-btn-delete"
                                            onClick={(clickEv) => handleDeleteAnketo(event, clickEv)}
                                            disabled={deletingId === event.id}
                                        >
                                            {deletingId === event.id ? "削除中…" : "アンケート削除"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ページネーション */}
            {totalPages > 1 && (
                <div className="el-pagination">
                    <button className="el-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹ 前</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <button key={p} className={`el-page-btn ${p === currentPage ? "active" : ""}`} onClick={() => setCurrentPage(p)}>{p}</button>
                    ))}
                    <button className="el-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>次 ›</button>
                </div>
            )}
        </div>
    );
};

export default EventAnketoMan;
